import { defineStore } from 'pinia';
import { api } from '@/services/http.service';
import { ElNotification } from 'element-plus';
import {
  PaymentMethodExceptionLimit,
  ExtendedPaymentMethodExceptionLimit,
  PaymentMethodGeneralLimit,
  PaymentMethodExceptionLimitCreateModel,
  PaymentMethodExceptionLimitUpdateModel,
  PlayerTag,
  createDefaultExceptionLimit,
  PaymentMethodExceptionLimitType,
} from '@/models/ps-management/exception-limit.model';
import {
  DEFAULT_GEO_PAYMENT_METHOD_FORM,
  GeoPaymentMethodItem,
  GlobalPaymentMethodItem,
} from '@/models/ps-management/ps-management.model';

interface PaymentMethodsExceptionLimitStore {
  currentPaymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem;
  exceptionLimitList: PaymentMethodExceptionLimit[];
  generalLimitList: PaymentMethodGeneralLimit[];
  isExceptionLimitListLoading: boolean;
  isUpdatingExceptionLimit: boolean;
  isExceptionLimitSettingsSidebarVisible: boolean;
  paymentMethodOptionType: PaymentMethodExceptionLimitType;
}

export const usePaymentMethodsExceptionLimitStore = defineStore({
  id: 'PaymentMethodsExceptionLimitStore',
  state: (): PaymentMethodsExceptionLimitStore => ({
    currentPaymentMethod: structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM),
    exceptionLimitList: [],
    generalLimitList: [],
    isExceptionLimitListLoading: false,
    isUpdatingExceptionLimit: false,
    isExceptionLimitSettingsSidebarVisible: false,
    paymentMethodOptionType: PaymentMethodExceptionLimitType.DEPOSIT,
  }),
  getters: {
    extendedPaymentMethodExceptionLimitList(
      state: PaymentMethodsExceptionLimitStore,
    ): ExtendedPaymentMethodExceptionLimit[] {
      return state.generalLimitList
        .map((generalLimit: PaymentMethodGeneralLimit) => {
          const relatedExceptionList = this.exceptionLimitList
            .filter((exception: PaymentMethodExceptionLimit) =>
              exception.currency === generalLimit.currency &&
              exception.type === state.paymentMethodOptionType,
            );

          return {
            ...generalLimit,
            limitList: relatedExceptionList,
          };
        });
    },
    tagsByCurrency(state: PaymentMethodsExceptionLimitStore): Record<string, PlayerTag[]> {
      const result: Record<string, PlayerTag[]> = {};

      state.exceptionLimitList.forEach((limit) => {
        if (!result[limit.currency]) {
          result[limit.currency] = [];
        }
        result[limit.currency].push(...limit.playerTags);
      });

      return result;
    },
    currentPaymentMethodId(state: PaymentMethodsExceptionLimitStore): string {
      return state.currentPaymentMethod.billingPaymentMethodSettingsId;
    },
    exceptionLimitHeader(state: PaymentMethodsExceptionLimitStore): string {
      const headerListMap: Record<string, string> = {
        'geo-deposit': 'Exception deposit limits',
        'geo-withdrawal': 'Exception withdrawal limits',
        'global-deposit': 'Global exception deposit limits',
        'global-withdrawal': 'Global exception withdrawal limits',
      };

      const methodScope = 'type' in state.currentPaymentMethod ? 'geo' : 'global';
      const methodType =
        state.paymentMethodOptionType === PaymentMethodExceptionLimitType.DEPOSIT ? 'deposit' : 'withdrawal';

      return headerListMap[`${methodScope}-${methodType}`];
    },
  },
  actions: {
    setExceptionLimitLoading(isLoading: boolean): void {
      this.isExceptionLimitListLoading = isLoading;
    },
    setCurrentPaymentMethod(paymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem): void {
      this.currentPaymentMethod = paymentMethod;
    },
    setUpdatingExceptionLimit(isUpdating: boolean): void {
      this.isUpdatingExceptionLimit = isUpdating;
    },
    setPaymentMethodOptionType(operationType: PaymentMethodExceptionLimitType): void {
      this.paymentMethodOptionType = operationType;
    },
    addExceptionLimitItem(currency: string): void {
      this.exceptionLimitList.push({
        ...createDefaultExceptionLimit(this.paymentMethodOptionType),
        currency,
        id: '',
      });
    },
    getPaymentMethodExceptionLimit(paymentMethodId: string): Promise<PaymentMethodExceptionLimit[]> {
      if (!paymentMethodId) {
        return Promise.resolve(this.exceptionLimitList);
      }

      this.setExceptionLimitLoading(true);

      return api.psManagement.getPaymentMethodExceptionLimitListRequest
        .perform(paymentMethodId)
        .then((exceptionLimitList: PaymentMethodExceptionLimit[]) => {
          this.exceptionLimitList = exceptionLimitList;

          return exceptionLimitList;
        })
        .finally(() => {
          this.setExceptionLimitLoading(false);
        });
    },
    getPaymentMethodGeneralLimit(paymentMethodId: string): Promise<PaymentMethodGeneralLimit[]> {
      if (!paymentMethodId) {
        return Promise.resolve(this.generalLimitList);
      }

      this.setExceptionLimitLoading(true);

      return api.psManagement.getPaymentMethodGeneralLimitListRequest
        .perform(paymentMethodId)
        .then((generalLimitList: PaymentMethodGeneralLimit[]) => {
          this.generalLimitList = generalLimitList;

          return generalLimitList;
        })
        .finally(() => {
          this.setExceptionLimitLoading(false);
        });
    },
    addPaymentMethodExceptionLimit(exceptionLimit: PaymentMethodExceptionLimitCreateModel): Promise<void> {
      this.setUpdatingExceptionLimit(true);

      return api.psManagement.addPaymentMethodExceptionLimitRequest
        .perform({
          paymentMethodSettingsId: this.currentPaymentMethodId,
          paymentMethodExceptionData: exceptionLimit,
        })
        .then(() => {
          this.getPaymentMethodExceptionLimit(this.currentPaymentMethodId);

          ElNotification({
            title: 'Success',
            message: 'Exception limit has been successfully created',
            type: 'success',
          });
        })
        .finally(() => {
          this.setUpdatingExceptionLimit(false);
        });
    },
    updatePaymentMethodExceptionLimit(exceptionLimit: PaymentMethodExceptionLimitUpdateModel): Promise<void> {
      this.setUpdatingExceptionLimit(true);

      return api.psManagement.updatePaymentMethodExceptionLimitRequest
        .perform({
          ...exceptionLimit,
          paymentMethodSettingsId: this.currentPaymentMethodId,
        })
        .then(() => {
          this.getPaymentMethodExceptionLimit(this.currentPaymentMethodId);

          ElNotification({
            title: 'Success',
            message: 'Exception limit has been successfully updated',
            type: 'success',
          });
        })
        .finally(() => {
          this.setUpdatingExceptionLimit(false);
        });
    },
    deletePaymentMethodExceptionLimit(exceptionLimitId: string): Promise<void> {
      this.setUpdatingExceptionLimit(true);

      return api.psManagement.deletePaymentMethodExceptionLimitRequest
        .perform({
          paymentMethodSettingsId: this.currentPaymentMethodId,
          exceptionLimitId,
        })
        .then(() => {
          this.getPaymentMethodExceptionLimit(this.currentPaymentMethodId);

          ElNotification({
            title: 'Success',
            message: 'Exception limit has been successfully deleted',
            type: 'success',
          });
        })
        .finally(() => {
          this.setUpdatingExceptionLimit(false);
        });
    },
    setExceptionLimitSettingsSidebarVisible(isVisible: boolean): void {
      this.isExceptionLimitSettingsSidebarVisible = isVisible;
    },
    openExceptionLimitSettings(
      paymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem,
      operationType: PaymentMethodExceptionLimitType,
    ): void {
      this.setPaymentMethodOptionType(operationType);
      this.setExceptionLimitSettingsSidebarVisible(true);
      this.setCurrentPaymentMethod(paymentMethod);
    },
    closeExceptionLimitSettings(): void {
      this.setExceptionLimitSettingsSidebarVisible(false);
      this.setCurrentPaymentMethod(structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM));
      this.setPaymentMethodOptionType(PaymentMethodExceptionLimitType.DEPOSIT);
    },
  },
});
